import { CONFIG, getMarket, isLegAllowed, legMultiplier, MARKET_LIST, type Fixture, type Outcome } from '@h2h/shared';
import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, Lock } from '../../../ui/icons';
import { cn } from '../../../../lib/cn';
import { signedPoints } from '../../../../lib/format';
import { teamCode, shortTeam } from '../../../../lib/teams';
import { TeamBadge } from '../../../TeamBadge';
import { CARD_AT_RISK, hasLeg, legsAtMax, type DraftCard } from '../../../../lib/picks';
import { useHaptics } from '../../../../hooks/useHaptics';
import { timeLabel } from '../../../../screens/play/helpers';
import { ScorerPickerSheet } from '../../ScorerPickerSheet';
import type { ScorerOptions } from '../types';
import { BASE } from '../tokens';
import { BaseButton } from './BaseButton';
import { LegRow } from './LegRow';

function step2Hint(legCount: number, maxed: boolean): string {
  if (maxed) {
    return `All ${CONFIG.COMBO.MAX_LEGS} stacked — every boost must land or your ${BASE} shrinks. No free boosts.`;
  }
  if (legCount === 0) {
    return `Optional. A clean ${BASE} is safe — each boost can multiply it, but a miss shrinks it below ${BASE}.`;
  }
  return `Land them all and your ${BASE} multiplies. Miss one and it shrinks below ${BASE} — boosts aren't free.`;
}

export function CardBuilder({
  fixture,
  card,
  legCatalog,
  statsEnabled = true,
  scorer,
  flag = false,
  frozen = false,
  onBack,
  onChange
}: {
  fixture: Fixture;
  card: DraftCard;
  legCatalog: readonly string[];
  statsEnabled?: boolean;
  scorer?: ScorerOptions | null;
  flag?: boolean;
  frozen?: boolean;
  onBack: () => void;
  onChange: (next: DraftCard) => void;
}) {
  const { impact } = useHaptics();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [scorerOpen, setScorerOpen] = useState(false);
  useEffect(() => {
    setExpanded(null);
    setScorerOpen(false);
  }, [fixture.fixtureId]);

  const home = teamCode(fixture.homeTeam, fixture.homeCode);
  const away = teamCode(fixture.awayTeam, fixture.awayCode);
  const homeShort = shortTeam(fixture.homeTeam);
  const awayShort = shortTeam(fixture.awayTeam);
  const ringClass = flag ? 'ring-1 ring-inset ring-black/10' : 'border border-black/[0.08]';
  const atMax = legsAtMax(card);
  const step2Help = step2Hint(card.legs.length, atMax);

  const scorerOn = legCatalog.includes('scorer') && !!scorer && (scorer.homePlayers.length > 0 || scorer.awayPlayers.length > 0);
  const scorerLeg = card.legs.find((l) => l.marketId === 'scorer');

  const catalogRows = useMemo(() => {
    // `isLegAllowed` is the shared rule the server enforces too, so we never offer a
    // leg the submit would reject: always-on (score/half-time) boosts show on every
    // fixture, while coverage legs (events/stats) need the fixture's frozen catalog as
    // proof. `statsEnabled` stays a belt-and-suspenders guard for the stats-sourced
    // legs (corners + most cards) — it must NOT gate `redCard`, which is events-sourced.
    return MARKET_LIST.filter((m) => !m.anchor)
      .map((m) => ({
        id: m.id,
        gated: !isLegAllowed(m.id, legCatalog) || (m.requires === 'stats' && !statsEnabled),
        // Headline ×N (the badge before a side is picked): flat markets use their
        // multiplier, per-outcome markets their biggest option. Sort ascending so the
        // safest boosts sit at the top and the hardest (highest ×) sink to the bottom.
        headline: legMultiplier(m.id)
      }))
      .sort((a, b) => a.headline - b.headline);
  }, [legCatalog, statsEnabled]);

  const setOutcome = (o: Outcome) => {
    if (frozen) return;
    impact('selection');
    onChange({ ...card, outcome: card.outcome === o ? null : o });
  };
  const setLeg = (marketId: string, selection: string, label?: string) => {
    if (frozen) return;
    const others = card.legs.filter((l) => l.marketId !== marketId);
    if (!hasLeg(card, marketId) && others.length >= CONFIG.COMBO.MAX_LEGS) return;
    impact('light');
    onChange({ ...card, legs: [...others, { marketId, selection, ...(label ? { label } : {}) }] });
  };
  const removeLeg = (marketId: string) => {
    if (frozen) return;
    impact('light');
    onChange({ ...card, legs: card.legs.filter((l) => l.marketId !== marketId) });
    setExpanded((e) => (e === marketId ? null : e));
  };
  const toggleLeg = (marketId: string) => {
    if (hasLeg(card, marketId)) {
      removeLeg(marketId);
      return;
    }
    const m = getMarket(marketId);
    if (m?.kind === 'exact' && !atMax) setLeg(marketId, '0-0');
    else if (m?.kind === 'binary' && m.options?.[0]) setLeg(marketId, m.options[0].value);
    else if (m?.kind === 'ternary' && m.options?.[0]) setLeg(marketId, m.options[0].value);
    else if (m?.kind === 'band' && m.options?.[0]) setLeg(marketId, m.options[0].value);
    setExpanded((e) => (e === marketId ? null : marketId));
  };

  const drawBadge = (
    <span className="flex justify-center">
      <TeamBadge
        code={home}
        logo={fixture.homeLogo}
        name={fixture.homeTeam}
        flag={flag}
        sizeClass="h-[34px] w-[34px]"
        roundedClass="rounded-[8px]"
        textClass="text-[8px]"
        className={ringClass}
      />
      <TeamBadge
        code={away}
        logo={fixture.awayLogo}
        name={fixture.awayTeam}
        flag={flag}
        sizeClass="h-[34px] w-[34px]"
        roundedClass="rounded-[8px]"
        textClass="text-[8px]"
        className={cn('-ml-[10px]', ringClass)}
      />
    </span>
  );

  return (
    <div className="pb-4">
      <div className="flex items-center gap-[11px] pb-3.5 pt-2">
        <button type="button" onClick={onBack} className="tap -ml-1 grid place-items-center" aria-label="Back to round">
          <ChevronLeft width={24} height={24} strokeWidth={2.4} className="text-ink" />
        </button>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="flex shrink-0">
            <TeamBadge code={home} logo={fixture.homeLogo} name={fixture.homeTeam} flag={flag} sizeClass="h-[26px] w-[26px]" roundedClass="rounded-[7px]" textClass="text-[7px]" className={ringClass} />
            <TeamBadge code={away} logo={fixture.awayLogo} name={fixture.awayTeam} flag={flag} sizeClass="h-[26px] w-[26px]" roundedClass="rounded-[7px]" textClass="text-[7px]" className={cn('-ml-[7px]', ringClass)} />
          </div>
          <div className="min-w-0">
            <div className="truncate text-[15px] font-extrabold tracking-[-0.02em] text-ink">
              {fixture.homeTeam} v {fixture.awayTeam}
            </div>
            <div className="text-[11px] font-semibold text-[#8A8A92]">
              {timeLabel(fixture.kickoffAt)}
              {frozen ? '' : ' · locks at kickoff'}
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between px-1 pb-1">
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#0A9A72]">Step 1/2 · who wins?</div>
            <span className="text-[16px] font-extrabold tracking-[-0.025em] text-ink">Pick your result</span>
          </div>
          <span className="rounded-[7px] bg-[rgba(17,179,107,0.12)] px-2 py-1 text-[11px] font-extrabold text-[#0A9A72]">{BASE} PTS</span>
        </div>
        <p className="px-1 pb-[11px] text-[11.5px] font-medium leading-[1.4] text-[#8A8A92]">
          Right keeps your {BASE} · wrong loses {BASE}. This is the gamble.
        </p>
        <div className="flex gap-2">
          <BaseButton
            active={card.outcome === 'HOME'}
            badge={
              <TeamBadge
                code={home}
                logo={fixture.homeLogo}
                name={fixture.homeTeam}
                flag={flag}
                sizeClass="h-[40px] w-[40px]"
                roundedClass="rounded-[9px]"
                textClass="text-[9px]"
                className={ringClass}
              />
            }
            title={homeShort}
            onClick={() => setOutcome('HOME')}
          />
          <BaseButton active={card.outcome === 'DRAW'} badge={drawBadge} title="Draw" onClick={() => setOutcome('DRAW')} />
          <BaseButton
            active={card.outcome === 'AWAY'}
            badge={
              <TeamBadge
                code={away}
                logo={fixture.awayLogo}
                name={fixture.awayTeam}
                flag={flag}
                sizeClass="h-[40px] w-[40px]"
                roundedClass="rounded-[9px]"
                textClass="text-[9px]"
                className={ringClass}
              />
            }
            title={awayShort}
            onClick={() => setOutcome('AWAY')}
          />
        </div>

        {card.outcome == null ? (
          // Locked state: one compact strip directly under the base picker so the
          // "pick a winner first" lock is always visible without scrolling — no tall
          // preview rows or full-width panel pushing it below the fold.
          <div className="mt-4 flex items-center gap-3 rounded-[16px] border border-dashed border-black/[0.16] bg-[#F7F7FA] px-4 py-[13px]">
            <span className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-[12px] bg-night">
              <Lock width={18} height={18} strokeWidth={2.3} className="text-white" />
            </span>
            <div className="min-w-0">
              <div className="text-[14px] font-extrabold tracking-[-0.01em] text-ink">Pick a winner to unlock boosts</div>
              <p className="mt-0.5 text-[11.5px] font-medium leading-[1.4] text-[#8A8A92]">
                Your 1X2 base qualifies the card — busts to <strong className="text-[#EF4135]">{signedPoints(CARD_AT_RISK)}</strong> if it&apos;s wrong.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between px-1 pb-1 pt-5">
              <div>
                <div className="text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#0A9A72]">Step 2/2 · add boosts</div>
                <span className="text-[16px] font-extrabold tracking-[-0.025em] text-ink">Stack optional boosts</span>
              </div>
              <span
                className={cn(
                  'num rounded-[7px] px-2 py-1 text-[11px] font-extrabold',
                  atMax ? 'bg-[rgba(239,65,53,0.1)] text-[#EF4135]' : 'bg-[rgba(17,179,107,0.12)] text-[#0A9A72]'
                )}
              >
                {card.legs.length}/{CONFIG.COMBO.MAX_LEGS}
              </span>
            </div>
            <p className="px-1 pb-3 text-[11.5px] font-medium leading-[1.4] text-[#8A8A92]">{step2Help}</p>
            <div className="flex flex-col gap-2">
              {catalogRows.map(({ id, gated }) => (
                <LegRow
                  key={id}
                  marketId={id}
                  card={card}
                  fixture={fixture}
                  expanded={expanded === id}
                  disabled={frozen || atMax}
                  gated={gated}
                  onToggle={() => toggleLeg(id)}
                  onSetLeg={(selection, label) => setLeg(id, selection, label)}
                  onRemoveLeg={() => removeLeg(id)}
                  onOpenScorer={() => {
                    if (!scorerOn || frozen || (atMax && !hasLeg(card, 'scorer'))) return;
                    impact('light');
                    setScorerOpen(true);
                  }}
                  scorerName={id === 'scorer' ? scorerLeg?.label : undefined}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {scorer && scorerOn && (
        <ScorerPickerSheet
          open={scorerOpen}
          fixture={fixture}
          homePlayers={scorer.homePlayers}
          awayPlayers={scorer.awayPlayers}
          selectedId={scorerLeg?.selection}
          flag={flag}
          tone="light"
          onClose={() => setScorerOpen(false)}
          onSelect={(player) => {
            if (player) setLeg('scorer', player.id, player.name);
            else removeLeg('scorer');
            setScorerOpen(false);
          }}
        />
      )}
    </div>
  );
}
